/**
 * CoinSing release version rule (KAN-270 / KAN-273 / KAN-431 / KAN-271):
 * Plain major.minor.patch, ASCII digits, no leading zeros, no pre-release or build suffix,
 * components of at most 9 digits.
 * Pinned by shared/coinsing-version-golden-vectors.json.
 */

export const COINSING_VERSION_GRAMMAR = /^(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})$/;

export function isValidVersion(text: unknown): text is string {
  return typeof text === 'string' && COINSING_VERSION_GRAMMAR.test(text);
}

export function parseVersion(text: unknown): [number, number, number] | null {
  if (!isValidVersion(text)) return null;
  const parts = text.split('.').map(Number);
  return [parts[0], parts[1], parts[2]];
}

/**
 * Returns -1 if a < b, 0 if a === b, 1 if a > b.
 * Throws an Error on an invalid version string.
 */
export function compareVersions(a: string, b: string): -1 | 0 | 1 {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (!pa) throw new Error(`Not a valid release version: ${JSON.stringify(a)}`);
  if (!pb) throw new Error(`Not a valid release version: ${JSON.stringify(b)}`);
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i] < pb[i] ? -1 : 1;
  }
  return 0;
}

/**
 * Normalizes installed version as reported by a kiosk (`appVersion`).
 * Accepts major.minor.patch, or normalizes a 4-part assembly version (e.g. 1.2.3.0 -> 1.2.3).
 * Anything else returns null (unknown).
 */
export function normalizeInstalledVersion(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (isValidVersion(trimmed)) return trimmed;
  const match4 = /^(0|[1-9][0-9]{0,8})\.(0|[1-9][0-9]{0,8})\.(0|[1-9][0-9]{0,8})\.0$/.exec(trimmed);
  if (match4) return `${match4[1]}.${match4[2]}.${match4[3]}`;
  const match2 = /^(0|[1-9][0-9]{0,8})\.(0|[1-9][0-9]{0,8})$/.exec(trimmed);
  if (match2) return `${match2[1]}.${match2[2]}.0`;
  return null;
}

/** What the App Version column shows for a reported `appVersion` (KAN-433 AC-3). */
export interface InstalledVersionView {
  /** `version`: a valid major.minor.patch (shown as `v{text}`); `raw`: reported but not that form (shown as typed); `unknown`: nothing reported. */
  kind: 'version' | 'raw' | 'unknown';
  /** The version, or the raw reported text; empty for `unknown`. */
  text: string;
}

/**
 * Classifies a reported value. Only an exact major.minor.patch counts as a version: `1.0`, `1.2.3.4`, `v1.2.3`, ` 1.2.3`
 * are shown raw and sort with Unknown. No normalising here (the gateway already normalised 4-part values before storing).
 */
export function describeInstalledVersion(raw: unknown): InstalledVersionView {
  if (raw === null || raw === undefined) return { kind: 'unknown', text: '' };
  const text = String(raw);
  if (text.trim() === '') return { kind: 'unknown', text: '' };
  return isValidVersion(text) ? { kind: 'version', text } : { kind: 'raw', text };
}

/**
 * Sort comparator for the App Version column. Real versions compare numerically (1.10.0 > 1.9.0) in the chosen direction;
 * everything else (raw, empty) is grouped AFTER the real versions in both directions; ties (including all non-versions) are
 * broken by deviceId ascending, so the order is deterministic. Never throws.
 */
export function compareInstalledVersionsForSort(
  a: { appVersion?: unknown; deviceId?: string },
  b: { appVersion?: unknown; deviceId?: string },
  direction: 'asc' | 'desc'
): number {
  const va = describeInstalledVersion(a.appVersion);
  const vb = describeInstalledVersion(b.appVersion);
  const knownA = va.kind === 'version';
  const knownB = vb.kind === 'version';
  if (knownA && !knownB) return -1;
  if (!knownA && knownB) return 1;
  if (knownA && knownB) {
    const c = compareVersions(va.text, vb.text);
    if (c !== 0) return direction === 'asc' ? c : -c;
  }
  return (a.deviceId || '').localeCompare(b.deviceId || '');
}

export interface VersionCatalogEntry {
  id?: string;
  version: string;
  isValid: boolean;
  environment?: string;
  publishedAt?: any;
  publishedBy?: string;
  releaseNote?: string;
  releaseNoteUpdatedAt?: string;
  releaseNoteUpdatedBy?: string;
  sha256?: string;
  sizeBytes?: number;
  fileCount?: number;
  buildConfiguration?: string;
  gitCommit?: string;
  storagePath?: string;
  manifestPath?: string;
  /** KAN-578: 'deleting' marks an interrupted delete (only Retry delete is offered). */
  deleteState?: string;
}

/**
 * Sorts catalog entries descending:
 * - Valid semver versions are sorted descending by numerical major.minor.patch.
 * - Invalid versions are placed at the very end with isValid: false.
 */
export function sortCatalogVersionsDescending<T extends { version: string }>(entries: T[]): (T & { isValid: boolean })[] {
  const valid: (T & { isValid: boolean })[] = [];
  const invalid: (T & { isValid: boolean })[] = [];

  for (const entry of entries) {
    if (isValidVersion(entry.version)) {
      valid.push({ ...entry, isValid: true });
    } else {
      invalid.push({ ...entry, isValid: false });
    }
  }

  valid.sort((a, b) => compareVersions(b.version, a.version));
  return [...valid, ...invalid];
}
